import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";

import { getFeatureFlag } from "@/shared/helpers/launch-darkly-server-helper";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import DefaultLayout from "@/layouts/default";

import { getBuilderMessages, getBuilderNavLinks } from "@/pages/api/builder/builder";
import { fetchHelpArticle } from "@/pages/api/help/get";
import { getFooterNavigation, getHeaderNavigation } from "@/pages/api/store-directory/navigation";

import { ArticlePage } from "@/components/help/article/article";
import { HelpPage } from "@/components/help/landing/landing";
import { PublicArticle } from "@/components/help/types/article";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    helpData: PublicArticle | undefined;
    articleToken: string;
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps;

init(BuilderModel.MESSAGE);

export const getServerSideProps: GetServerSideProps<Props> = async ({
    locale,
    resolvedUrl,
    params,
}) => {
    const isEnabled = await getFeatureFlag("help-centre-enabled", false, locale);

    if (!isEnabled) {
        return { redirect: { destination: `/${locale}`, permanent: false } };
    }

    const articleParam = Array.isArray(params.article) ? params.article[0] : params.article;
    const token = articleParam.match(/^(\d+)/)?.[1] ?? articleParam;

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale, { url: resolvedUrl }),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
        articleData: () => fetchHelpArticle(token, locale),
    });

    return {
        props: {
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            helpData: parallelProps.articleData.article,
            articleToken: token,
            ...parallelProps,
        },
    };
};

const HelpArticle = ({
    locale,
    footerLocalNavItems,
    footerRegionalNavItems,
    helpData,
    articleToken,
}: Props): React.ReactNode => {
    const { t } = useTranslation();

    useEffect(() => {
        document.body.classList.add("helpPage");
        return () => document.body.classList.remove("helpPage");
    }, []);

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={[]}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={[]}
            builderMessages={[]}
            title={helpData?.metaTitle || helpData?.title || t("title")}
            description={helpData?.metaDescription || t("metaDescription")}
        >
            {!helpData?.bodyHtml && helpData?.childArticles?.length > 0 ? (
                <HelpPage helpData={helpData} articleToken={articleToken} />
            ) : (
                <ArticlePage
                    title={helpData?.title || ""}
                    bodyHtml={helpData?.bodyHtml || ""}
                    childArticles={helpData?.childArticles}
                    articleToken={articleToken}
                />
            )}
        </DefaultLayout>
    );
};

export default HelpArticle;
